import { useState, useEffect, useCallback } from 'react';

// ─── Shape ──────────────────────────────────────────────────────────────────
export interface AppConfig {
  dateFormat: string;
  timeFormat: '12h' | '24h';   // the Date & Time Zone page only offers these two
  timezone: string;
}

const STORAGE_KEY = 'crm_general_settings';
const EVENT_NAME = 'general-settings-changed';

const DEFAULT_CONFIG: AppConfig = {
  dateFormat: 'dd/MM/yyyy',
  timeFormat: '12h',
  timezone: 'Asia/Kolkata',
};

// ─── Helpers (synchronous, usable outside React) ────────────────────────────
function readConfig(): AppConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_CONFIG, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return DEFAULT_CONFIG;
}

/** Synchronous read — for use outside React (e.g. istDate.ts). */
export function getConfig(): AppConfig {
  return readConfig();
}

/** Write config and notify all listeners (instant reflection). */
export function setConfig(config: AppConfig): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  window.dispatchEvent(new Event(EVENT_NAME));
}

// ─── React hook — re-renders when config changes ────────────────────────────
export function useConfig(): AppConfig {
  const [config, setLocal] = useState<AppConfig>(readConfig);

  const refresh = useCallback(() => setLocal(readConfig()), []);

  useEffect(() => {
    window.addEventListener(EVENT_NAME, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(EVENT_NAME, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  return config;
}
